import React from 'react'
import { ChevronLeft, X } from 'lucide-react'

export default function EffectsPanel({ isOpen, onClose, selectedElement, template_content, settemplate_content }) {
  if (!isOpen) return null;
  const applyPatch = (patch) => {
    const next = { ...template_content }
    const page = next.pages?.[0]
    for (const p of next.pages || []) {
      const idx = (p.items||[]).findIndex(it => it.id === selectedElement)
      if (idx >= 0) {
        const it = p.items[idx]
        it.effects = it.effects || []
        Object.assign(it, patch)
        break
      }
    }
    settemplate_content(next)
  }

  const addMotion = () => {
    const motion = { type: 'motion', easing: 'easeInOut', from: { x: 0, y: 0, scale: 1, opacity: 1 }, to: { x: 200, y: 0, scale: 1, opacity: 1 } }
    addEffect(motion)
  }

  const addFade = () => {
    const fade = { type: 'fade', in: 0.5, out: 0.5 }
    addEffect(fade)
  }

  const addZoom = () => {
    const zoom = { type: 'zoom', easing: 'easeInOut', from: { scale: 0.5 }, to: { scale: 1.0 } }
    addEffect(zoom)
  }

  const addRotate = () => {
    const rotate = { type: 'rotate', easing: 'easeInOut', from: { rotation: 0 }, to: { rotation: 360 } }
    addEffect(rotate)
  }

  const addSlide = () => {
    const slide = { type: 'slide', easing: 'easeInOut', direction: 'left', distance: 200 }
    addEffect(slide)
  }

  const addBounce = () => {
    const bounce = { type: 'bounce', easing: 'easeOut', intensity: 1.2, bounces: 3 }
    addEffect(bounce)
  }

  const addPulse = () => {
    const pulse = { type: 'pulse', easing: 'easeInOut', minScale: 0.9, maxScale: 1.1, pulses: 2 }
    addEffect(pulse)
  }

  // Text Animation Effects
  const addTypewriter = () => {
    const typewriter = { type: 'typewriter', easing: 'linear', speed: 50 }
    addEffect(typewriter)
  }

  const addAscend = () => {
    const ascend = { type: 'ascend', easing: 'easeOut', distance: 50, stagger: 0.1 }
    addEffect(ascend)
  }

  const addShift = () => {
    const shift = { type: 'shift', easing: 'easeInOut', direction: 'horizontal', distance: 30, stagger: 0.05 }
    addEffect(shift)
  }

  const addMerge = () => {
    const merge = { type: 'merge', easing: 'easeOut', direction: 'center', distance: 100, stagger: 0.08 }
    addEffect(merge)
  }

  const addBlock = () => {
    const block = { type: 'block', easing: 'easeInOut', blockSize: 'word', stagger: 0.2 }
    addEffect(block)
  }

  const addBurst = () => {
    const burst = { type: 'burst', easing: 'easeOut', intensity: 1.5, stagger: 0.03 }
    addEffect(burst)
  }

  const addBounceText = () => {
    const bounceText = { type: 'bounceText', easing: 'easeOut', height: 20, stagger: 0.1 }
    addEffect(bounceText)
  }

  const addRoll = () => {
    const roll = { type: 'roll', easing: 'easeInOut', direction: 'clockwise', stagger: 0.1 }
    addEffect(roll)
  }

  const addSkate = () => {
    const skate = { type: 'skate', easing: 'easeOut', distance: 40, wobble: 10, stagger: 0.05 }
    addEffect(skate)
  }

  const addSpread = () => {
    const spread = { type: 'spread', easing: 'easeOut', distance: 20, stagger: 0.05 }
    addEffect(spread)
  }

  const addClarify = () => {
    const clarify = { type: 'clarify', easing: 'easeInOut', blurAmount: 10, stagger: 0.1 }
    addEffect(clarify)
  }
  const addEffect = (eff) => {
    const next = { ...template_content }
    for (const p of next.pages || []) {
      const idx = (p.items||[]).findIndex(it => it.id === selectedElement)
      if (idx >= 0) {
        const it = p.items[idx]
        it.effects = it.effects || []
        // Default timing to element time
        const time = it.time || { start: 0, duration: 2 }
        it.effects.push({ ...eff, start: time.start || 0, duration: time.duration || 2 })
        break
      }
    }
    settemplate_content(next)
  }

  // Add new video effects (Adjust, Transform, Transition, Blur, Dissolve)
  const addAdjustEffect = () => {
    const adjust = {
      type: 'adjust',
      enabled: true,
      easing: 'linear',
      brightness: 0,
      contrast: 0,
      saturation: 0,
      exposure: 0,
      highlights: 0,
      shadows: 0,
      whites: 0,
      blacks: 0
    }
    addEffect(adjust)
  }

  const addTransformEffect = () => {
    const transform = {
      type: 'transform',
      enabled: true,
      easing: 'linear',
      from: {
        positionX: 0,
        positionY: 0,
        scaleX: 1,
        scaleY: 1,
        rotation: 0,
        opacity: 100
      },
      to: {
        positionX: 0,
        positionY: 0,
        scaleX: 1,
        scaleY: 1,
        rotation: 0,
        opacity: 100
      },
      anchorPointX: 0.5,
      anchorPointY: 0.5
    }
    addEffect(transform)
  }

  const addTransitionEffect = () => {
    const transition = {
      type: 'transition',
      enabled: true,
      easing: 'linear',
      transitionType: 'crossfade',
      direction: 'left'
    }
    addEffect(transition)
  }

  const addBlurEffect = () => {
    const blur = {
      type: 'blur',
      enabled: true,
      easing: 'linear',
      blurType: 'gaussian',
      blurRadius: 10,
      angle: 0,
      centerX: 0.5,
      centerY: 0.5,
      distance: 10
    }
    addEffect(blur)
  }

  const addDissolveEffect = () => {
    const dissolve = {
      type: 'dissolve',
      enabled: true,
      easing: 'linear',
      fadeIn: 0.5,
      fadeOut: 0.5
    }
    addEffect(dissolve)
  }

  const removeEffect = (index) => {
    const next = { ...template_content }
    for (const p of next.pages || []) {
      const idx = (p.items||[]).findIndex(it => it.id === selectedElement)
      if (idx >= 0) {
        const it = p.items[idx]
        it.effects = (it.effects || []).filter((_,i)=>i!==index)
        break
      }
    }
    settemplate_content(next)
  }

  // Determine effects for the selected element
  let effects = []
  for (const p of template_content?.pages || []) {
    const it = (p.items||[]).find(i => i.id === selectedElement)
    if (it) { effects = it.effects || []; break }
  }

  return (
    <div className="fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-500">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">Item Effects & Motions</h2>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">

      {/* Basic Effect Buttons */}
      <div className="mb-3">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Basic Effects</h4>
        <div className="grid grid-cols-3 gap-2">
          <button className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700" onClick={addFade}>
            Fade
          </button>
          <button className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700" onClick={addMotion}>
            Motion
          </button>
          <button className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700" onClick={addZoom}>
            Zoom
          </button>
          <button className="px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700" onClick={addRotate}>
            Rotate
          </button>
          <button className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700" onClick={addSlide}>
            Slide
          </button>
          <button className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700" onClick={addBounce}>
            Bounce
          </button>
          <button className="px-2 py-1 text-xs bg-pink-600 text-white rounded hover:bg-pink-700" onClick={addPulse}>
            Pulse
          </button>
        </div>
      </div>

      {/* Video Effects */}
      <div className="mb-3">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Video Effects</h4>
        <div className="grid grid-cols-3 gap-2">
          <button className="px-2 py-1 text-xs bg-cyan-600 text-white rounded hover:bg-cyan-700" onClick={addAdjustEffect} title="Color adjustment">
            🎛️ Adjust
          </button>
          <button className="px-2 py-1 text-xs bg-violet-600 text-white rounded hover:bg-violet-700" onClick={addTransformEffect} title="Transform animation">
            🔄 Transform
          </button>
          <button className="px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700" onClick={addTransitionEffect} title="Transition effect">
            ✨ Transition
          </button>
          <button className="px-2 py-1 text-xs bg-slate-600 text-white rounded hover:bg-slate-700" onClick={addBlurEffect} title="Blur effect">
            🌫️ Blur
          </button>
          <button className="px-2 py-1 text-xs bg-rose-600 text-white rounded hover:bg-rose-700" onClick={addDissolveEffect} title="Dissolve transition">
            💫 Dissolve
          </button>
        </div>
      </div>

      {/* Text Animation Buttons */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Text Animations</h4>
        <div className="grid grid-cols-3 gap-2">
          <button className="px-2 py-1 text-xs bg-cyan-600 text-white rounded hover:bg-cyan-700" onClick={addTypewriter}>
            Typewriter
          </button>
          <button className="px-2 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700" onClick={addAscend}>
            Ascend
          </button>
          <button className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700" onClick={addShift}>
            Shift
          </button>
          <button className="px-2 py-1 text-xs bg-lime-600 text-white rounded hover:bg-lime-700" onClick={addMerge}>
            Merge
          </button>
          <button className="px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700" onClick={addBlock}>
            Block
          </button>
          <button className="px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700" onClick={addBurst}>
            Burst
          </button>
          <button className="px-2 py-1 text-xs bg-rose-600 text-white rounded hover:bg-rose-700" onClick={addBounceText}>
            Bounce Text
          </button>
          <button className="px-2 py-1 text-xs bg-violet-600 text-white rounded hover:bg-violet-700" onClick={addRoll}>
            Roll
          </button>
          <button className="px-2 py-1 text-xs bg-fuchsia-600 text-white rounded hover:bg-fuchsia-700" onClick={addSkate}>
            Skate
          </button>
          <button className="px-2 py-1 text-xs bg-sky-600 text-white rounded hover:bg-sky-700" onClick={addSpread}>
            Spread
          </button>
          <button className="px-2 py-1 text-xs bg-slate-600 text-white rounded hover:bg-slate-700" onClick={addClarify}>
            Clarify
          </button>
        </div>
      </div>
      {effects.length === 0 && <div className="text-sm text-gray-500">No effects. Click Add fade to create one.</div>}
      {effects.map((e, idx) => (
        <div key={idx} className="border rounded p-2 space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <div className="font-medium">{e.type}</div>
            <button className="px-2 py-1 text-xs bg-red-600 text-white rounded" onClick={()=>removeEffect(idx)}>Remove</button>
          </div>
          {e.type === 'fade' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Fade In(s)
                <input type="number" min="0" step="0.1" className="border px-2 py-1 w-full" value={e.in??0.5} onChange={evt=>{
                  const val = parseFloat(evt.target.value)||0
                  const next = { ...template_content }
                  for (const p of next.pages || []) {
                    const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                    if (i>=0) { p.items[i].effects[idx] = { ...e, in: val }; break }
                  }
                  settemplate_content(next)
                }} />
              </label>
              <label className="flex items-center gap-2">Fade Out(s)
                <input type="number" min="0" step="0.1" className="border px-2 py-1 w-full" value={e.out??0.5} onChange={evt=>{
                  const val = parseFloat(evt.target.value)||0
                  const next = { ...template_content }
                  for (const p of next.pages || []) {
                    const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                    if (i>=0) { p.items[i].effects[idx] = { ...e, out: val }; break }
                  }
                  settemplate_content(next)
                }} />
              </label>
            </div>
          )}

          {e.type === 'motion' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Start(s)
                <input type="number" min="0" step="0.1" className="border px-2 py-1 w-full" value={e.start??0} onChange={evt=>{
                  const val = parseFloat(evt.target.value)||0
                  const next = { ...template_content }
                  for (const p of next.pages || []) {
                    const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                    if (i>=0) { p.items[i].effects[idx] = { ...e, start: val }; break }
                  }
                  settemplate_content(next)
                }} />
              </label>
              <label className="flex items-center gap-2">Duration(s)
                <input type="number" min="0.1" step="0.1" className="border px-2 py-1 w-full" value={e.duration??2} onChange={evt=>{
                  const val = parseFloat(evt.target.value)||0.1
                  const next = { ...template_content }
                  for (const p of next.pages || []) {
                    const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                    if (i>=0) { p.items[i].effects[idx] = { ...e, duration: val }; break }
                  }
                  settemplate_content(next)
                }} />
              </label>
              <label className="flex items-center gap-2">Easing
                <select className="border px-2 py-1 w-full" value={e.easing||'linear'} onChange={evt=>{
                  const val = evt.target.value
                  const next = { ...template_content }
                  for (const p of next.pages || []) {
                    const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                    if (i>=0) { p.items[i].effects[idx] = { ...e, easing: val }; break }
                  }
                  settemplate_content(next)
                }}>
                  <option value="linear">linear</option>
                  <option value="easeIn">easeIn</option>
                  <option value="easeOut">easeOut</option>
                  <option value="easeInOut">easeInOut</option>
                </select>
              </label>
              <div className="grid grid-cols-4 gap-2 col-span-2">
                <label className="flex items-center gap-2">From X
                  <input type="number" className="border px-2 py-1 w-full" value={e.from?.x??0} onChange={evt=>{
                    const val = parseFloat(evt.target.value)||0
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, from: { ...(e.from||{}), x: val } }; break }
                    }
                    settemplate_content(next)
                  }} />
                </label>
                <label className="flex items-center gap-2">From Y
                  <input type="number" className="border px-2 py-1 w-full" value={e.from?.y??0} onChange={evt=>{
                    const val = parseFloat(evt.target.value)||0
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, from: { ...(e.from||{}), y: val } }; break }
                    }
                    settemplate_content(next)
                  }} />
                </label>
                <label className="flex items-center gap-2">From Scale
                  <input type="number" step="0.01" className="border px-2 py-1 w-full" value={e.from?.scale??1} onChange={evt=>{
                    const val = parseFloat(evt.target.value)||1
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, from: { ...(e.from||{}), scale: val } }; break }
                    }
                    settemplate_content(next)
                  }} />
                </label>
                <label className="flex items-center gap-2">From Opacity
                  <input type="number" step="0.01" min="0" max="1" className="border px-2 py-1 w-full" value={e.from?.opacity??1} onChange={evt=>{
                    const val = parseFloat(evt.target.value)
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, from: { ...(e.from||{}), opacity: isNaN(val)?1:val } }; break }
                    }
                    settemplate_content(next)
                  }} />
                </label>
                <label className="flex items-center gap-2">To X
                  <input type="number" className="border px-2 py-1 w-full" value={e.to?.x??0} onChange={evt=>{
                    const val = parseFloat(evt.target.value)||0
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, to: { ...(e.to||{}), x: val } }; break }
                    }
                    settemplate_content(next)
                  }} />
                </label>
                <label className="flex items-center gap-2">To Y
                  <input type="number" className="border px-2 py-1 w-full" value={e.to?.y??0} onChange={evt=>{
                    const val = parseFloat(evt.target.value)||0
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, to: { ...(e.to||{}), y: val } }; break }
                    }
                    settemplate_content(next)
                  }} />
                </label>
                <label className="flex items-center gap-2">To Scale
                  <input type="number" step="0.01" className="border px-2 py-1 w-full" value={e.to?.scale??1} onChange={evt=>{
                    const val = parseFloat(evt.target.value)||1
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, to: { ...(e.to||{}), scale: val } }; break }
                    }
                    settemplate_content(next)
                  }} />
                </label>
                <label className="flex items-center gap-2">To Opacity
                  <input type="number" step="0.01" min="0" max="1" className="border px-2 py-1 w-full" value={e.to?.opacity??1} onChange={evt=>{
                    const val = parseFloat(evt.target.value)
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, to: { ...(e.to||{}), opacity: isNaN(val)?1:val } }; break }
                    }
                    settemplate_content(next)
                  }} />
                </label>
              </div>
            </div>
          )}

          {/* Zoom Effect Controls */}
          {e.type === 'zoom' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">From Scale
                <input type="number" step="0.1" min="0" className="border px-2 py-1 w-full"
                  value={e.from?.scale ?? 0.5}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.5
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, from: { ...(e.from||{}), scale: val } }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">To Scale
                <input type="number" step="0.1" min="0" className="border px-2 py-1 w-full"
                  value={e.to?.scale ?? 1.0}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 1.0
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, to: { ...(e.to||{}), scale: val } }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Rotate Effect Controls */}
          {e.type === 'rotate' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">From Rotation
                <input type="number" className="border px-2 py-1 w-full"
                  value={e.from?.rotation ?? 0}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, from: { ...(e.from||{}), rotation: val } }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">To Rotation
                <input type="number" className="border px-2 py-1 w-full"
                  value={e.to?.rotation ?? 360}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 360
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, to: { ...(e.to||{}), rotation: val } }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Slide Effect Controls */}
          {e.type === 'slide' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Direction
                <select className="border px-2 py-1 w-full"
                  value={e.direction || 'left'}
                  onChange={evt => {
                    const val = evt.target.value
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, direction: val }; break }
                    }
                    settemplate_content(next)
                  }}>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                  <option value="up">Up</option>
                  <option value="down">Down</option>
                </select>
              </label>
              <label className="flex items-center gap-2">Distance
                <input type="number" className="border px-2 py-1 w-full"
                  value={e.distance ?? 200}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 200
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, distance: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Bounce Effect Controls */}
          {e.type === 'bounce' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Intensity
                <input type="number" step="0.1" min="1" max="2" className="border px-2 py-1 w-full"
                  value={e.intensity ?? 1.2}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 1.2
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, intensity: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Bounces
                <input type="number" min="1" max="10" className="border px-2 py-1 w-full"
                  value={e.bounces ?? 3}
                  onChange={evt => {
                    const val = parseInt(evt.target.value) || 3
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, bounces: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Pulse Effect Controls */}
          {e.type === 'pulse' && (
            <div className="grid grid-cols-3 gap-2">
              <label className="flex items-center gap-2">Min Scale
                <input type="number" step="0.1" min="0.1" max="1" className="border px-2 py-1 w-full"
                  value={e.minScale ?? 0.9}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.9
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, minScale: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Max Scale
                <input type="number" step="0.1" min="1" max="2" className="border px-2 py-1 w-full"
                  value={e.maxScale ?? 1.1}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 1.1
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, maxScale: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Pulses
                <input type="number" min="1" max="10" className="border px-2 py-1 w-full"
                  value={e.pulses ?? 2}
                  onChange={evt => {
                    const val = parseInt(evt.target.value) || 2
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, pulses: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Typewriter Effect Controls */}
          {e.type === 'typewriter' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Speed (ms)
                <input type="number" min="10" max="200" className="border px-2 py-1 w-full"
                  value={e.speed ?? 50}
                  onChange={evt => {
                    const val = parseInt(evt.target.value) || 50
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, speed: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Cursor
                <select className="border px-2 py-1 w-full"
                  value={e.showCursor ? 'true' : 'false'}
                  onChange={evt => {
                    const val = evt.target.value === 'true'
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, showCursor: val }; break }
                    }
                    settemplate_content(next)
                  }}>
                  <option value="true">Show</option>
                  <option value="false">Hide</option>
                </select>
              </label>
            </div>
          )}

          {/* Ascend Effect Controls */}
          {e.type === 'ascend' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Distance
                <input type="number" min="10" max="100" className="border px-2 py-1 w-full"
                  value={e.distance ?? 50}
                  onChange={evt => {
                    const val = parseInt(evt.target.value) || 50
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, distance: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Stagger
                <input type="number" step="0.01" min="0" max="0.5" className="border px-2 py-1 w-full"
                  value={e.stagger ?? 0.1}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.1
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, stagger: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Shift Effect Controls */}
          {e.type === 'shift' && (
            <div className="grid grid-cols-3 gap-2">
              <label className="flex items-center gap-2">Direction
                <select className="border px-2 py-1 w-full"
                  value={e.direction || 'horizontal'}
                  onChange={evt => {
                    const val = evt.target.value
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, direction: val }; break }
                    }
                    settemplate_content(next)
                  }}>
                  <option value="horizontal">Horizontal</option>
                  <option value="vertical">Vertical</option>
                </select>
              </label>
              <label className="flex items-center gap-2">Distance
                <input type="number" min="5" max="100" className="border px-2 py-1 w-full"
                  value={e.distance ?? 30}
                  onChange={evt => {
                    const val = parseInt(evt.target.value) || 30
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, distance: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Stagger
                <input type="number" step="0.01" min="0" max="0.2" className="border px-2 py-1 w-full"
                  value={e.stagger ?? 0.05}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.05
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, stagger: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Merge Effect Controls */}
          {e.type === 'merge' && (
            <div className="grid grid-cols-3 gap-2">
              <label className="flex items-center gap-2">Direction
                <select className="border px-2 py-1 w-full"
                  value={e.direction || 'center'}
                  onChange={evt => {
                    const val = evt.target.value
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, direction: val }; break }
                    }
                    settemplate_content(next)
                  }}>
                  <option value="center">Center</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
              </label>
              <label className="flex items-center gap-2">Distance
                <input type="number" min="20" max="200" className="border px-2 py-1 w-full"
                  value={e.distance ?? 100}
                  onChange={evt => {
                    const val = parseInt(evt.target.value) || 100
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, distance: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Stagger
                <input type="number" step="0.01" min="0" max="0.3" className="border px-2 py-1 w-full"
                  value={e.stagger ?? 0.08}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.08
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, stagger: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Block Effect Controls */}
          {e.type === 'block' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Block Size
                <select className="border px-2 py-1 w-full"
                  value={e.blockSize || 'word'}
                  onChange={evt => {
                    const val = evt.target.value
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, blockSize: val }; break }
                    }
                    settemplate_content(next)
                  }}>
                  <option value="character">Character</option>
                  <option value="word">Word</option>
                  <option value="line">Line</option>
                </select>
              </label>
              <label className="flex items-center gap-2">Stagger
                <input type="number" step="0.01" min="0" max="0.5" className="border px-2 py-1 w-full"
                  value={e.stagger ?? 0.2}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.2
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, stagger: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Burst Effect Controls */}
          {e.type === 'burst' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Intensity
                <input type="number" step="0.1" min="1" max="3" className="border px-2 py-1 w-full"
                  value={e.intensity ?? 1.5}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 1.5
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, intensity: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Stagger
                <input type="number" step="0.01" min="0" max="0.1" className="border px-2 py-1 w-full"
                  value={e.stagger ?? 0.03}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.03
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, stagger: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Bounce Text Effect Controls */}
          {e.type === 'bounceText' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Height
                <input type="number" min="5" max="50" className="border px-2 py-1 w-full"
                  value={e.height ?? 20}
                  onChange={evt => {
                    const val = parseInt(evt.target.value) || 20
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, height: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Stagger
                <input type="number" step="0.01" min="0" max="0.3" className="border px-2 py-1 w-full"
                  value={e.stagger ?? 0.1}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.1
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, stagger: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Roll Effect Controls */}
          {e.type === 'roll' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Direction
                <select className="border px-2 py-1 w-full"
                  value={e.direction || 'clockwise'}
                  onChange={evt => {
                    const val = evt.target.value
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, direction: val }; break }
                    }
                    settemplate_content(next)
                  }}>
                  <option value="clockwise">Clockwise</option>
                  <option value="counterclockwise">Counter-clockwise</option>
                </select>
              </label>
              <label className="flex items-center gap-2">Stagger
                <input type="number" step="0.01" min="0" max="0.3" className="border px-2 py-1 w-full"
                  value={e.stagger ?? 0.1}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.1
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, stagger: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Skate Effect Controls */}
          {e.type === 'skate' && (
            <div className="grid grid-cols-3 gap-2">
              <label className="flex items-center gap-2">Distance
                <input type="number" min="10" max="100" className="border px-2 py-1 w-full"
                  value={e.distance ?? 40}
                  onChange={evt => {
                    const val = parseInt(evt.target.value) || 40
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, distance: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Wobble
                <input type="number" min="0" max="30" className="border px-2 py-1 w-full"
                  value={e.wobble ?? 10}
                  onChange={evt => {
                    const val = parseInt(evt.target.value) || 10
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, wobble: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Stagger
                <input type="number" step="0.01" min="0" max="0.2" className="border px-2 py-1 w-full"
                  value={e.stagger ?? 0.05}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.05
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, stagger: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Spread Effect Controls */}
          {e.type === 'spread' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Distance
                <input type="number" min="5" max="50" className="border px-2 py-1 w-full"
                  value={e.distance ?? 20}
                  onChange={evt => {
                    const val = parseInt(evt.target.value) || 20
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, distance: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Stagger
                <input type="number" step="0.01" min="0" max="0.2" className="border px-2 py-1 w-full"
                  value={e.stagger ?? 0.05}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.05
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, stagger: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}

          {/* Clarify Effect Controls */}
          {e.type === 'clarify' && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">Blur Amount
                <input type="number" min="1" max="20" className="border px-2 py-1 w-full"
                  value={e.blurAmount ?? 10}
                  onChange={evt => {
                    const val = parseInt(evt.target.value) || 10
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, blurAmount: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
              <label className="flex items-center gap-2">Stagger
                <input type="number" step="0.01" min="0" max="0.3" className="border px-2 py-1 w-full"
                  value={e.stagger ?? 0.1}
                  onChange={evt => {
                    const val = parseFloat(evt.target.value) || 0.1
                    const next = { ...template_content }
                    for (const p of next.pages || []) {
                      const i = (p.items||[]).findIndex(x => x.id === selectedElement)
                      if (i>=0) { p.items[i].effects[idx] = { ...e, stagger: val }; break }
                    }
                    settemplate_content(next)
                  }} />
              </label>
            </div>
          )}
        </div>
      ))}
        </div>
      </div>
    </div>
  )
}

